export interface HeaderCallbacks {
  readonly onUndo: () => void;
  readonly onRedo: () => void;
  readonly onNew: () => void;
  readonly onClear: () => void;
  readonly onImport: () => void;
  readonly onExportPng: () => void;
  readonly onExportProject: () => void;
}

export class Header {
  private readonly element: HTMLElement;
  private readonly callbacks: HeaderCallbacks;

  private undoBtn!: HTMLButtonElement;
  private redoBtn!: HTMLButtonElement;
  private dirtyIndicator!: HTMLElement;
  private docTitle!: HTMLElement;

  constructor(container: HTMLElement, callbacks: HeaderCallbacks) {
    this.element = container;
    this.callbacks = callbacks;
    this.render();
  }

  private render(): void {
    this.element.innerHTML = "";
    this.element.className = "header-bar";

    // Brand Area
    const brand = document.createElement("div");
    brand.className = "header-brand";

    const titleContainer = document.createElement("div");
    titleContainer.className = "brand-title-group";

    const appName = document.createElement("span");
    appName.className = "brand-name";
    appName.textContent = "PIXELWERKSTATT";

    this.dirtyIndicator = document.createElement("span");
    this.dirtyIndicator.className = "dirty-indicator";
    this.dirtyIndicator.title = "Nicht gespeicherte Änderungen vorhanden";
    this.dirtyIndicator.style.display = "none";
    this.dirtyIndicator.textContent = "●";

    this.docTitle = document.createElement("span");
    this.docTitle.className = "brand-tagline";
    this.docTitle.textContent = "kleine Bilder, klare Ideen";

    titleContainer.appendChild(appName);
    titleContainer.appendChild(this.dirtyIndicator);
    titleContainer.appendChild(this.docTitle);
    brand.appendChild(titleContainer);
    this.element.appendChild(brand);

    // History Actions (Undo/Redo)
    const historyGroup = document.createElement("div");
    historyGroup.className = "header-group header-history";

    this.undoBtn = document.createElement("button");
    this.undoBtn.type = "button";
    this.undoBtn.className = "button button-ghost button-sm";
    this.undoBtn.title = "Rückgängig (Strg+Z)";
    this.undoBtn.setAttribute("aria-label", "Rückgängig machen");
    this.undoBtn.innerHTML = `<span class="btn-icon">↶</span> <span class="btn-text">Rückgängig</span>`;
    this.undoBtn.disabled = true;
    this.undoBtn.addEventListener("click", () => this.callbacks.onUndo());

    this.redoBtn = document.createElement("button");
    this.redoBtn.type = "button";
    this.redoBtn.className = "button button-ghost button-sm";
    this.redoBtn.title = "Wiederholen (Strg+Y / Strg+Umschalt+Z)";
    this.redoBtn.setAttribute("aria-label", "Wiederholen");
    this.redoBtn.innerHTML = `<span class="btn-icon">↷</span> <span class="btn-text">Wiederholen</span>`;
    this.redoBtn.disabled = true;
    this.redoBtn.addEventListener("click", () => this.callbacks.onRedo());

    historyGroup.appendChild(this.undoBtn);
    historyGroup.appendChild(this.redoBtn);
    this.element.appendChild(historyGroup);

    // Document Operations
    const actionsGroup = document.createElement("div");
    actionsGroup.className = "header-group header-actions";

    const newBtn = document.createElement("button");
    newBtn.type = "button";
    newBtn.className = "button button-ghost button-sm";
    newBtn.title = "Neue leere Leinwand anlegen";
    newBtn.innerHTML = `<span class="btn-icon">＋</span> <span class="btn-text">Neu</span>`;
    newBtn.addEventListener("click", () => this.callbacks.onNew());

    const clearBtn = document.createElement("button");
    clearBtn.type = "button";
    clearBtn.className = "button button-ghost button-sm";
    clearBtn.title = "Alle Pixel leeren";
    clearBtn.innerHTML = `<span class="btn-icon">⌫</span> <span class="btn-text">Leeren</span>`;
    clearBtn.addEventListener("click", () => this.callbacks.onClear());

    const importBtn = document.createElement("button");
    importBtn.type = "button";
    importBtn.className = "button button-secondary button-sm";
    importBtn.title = "Hex-Raster oder JSON importieren";
    importBtn.innerHTML = `<span class="btn-icon">📥</span> <span class="btn-text">Importieren</span>`;
    importBtn.addEventListener("click", () => this.callbacks.onImport());

    const exportProjectBtn = document.createElement("button");
    exportProjectBtn.type = "button";
    exportProjectBtn.className = "button button-secondary button-sm";
    exportProjectBtn.title = "Projekt als versionierte JSON-Datei herunterladen";
    exportProjectBtn.innerHTML = `<span class="btn-icon">📄</span> <span class="btn-text">Projekt speichern</span>`;
    exportProjectBtn.addEventListener("click", () => this.callbacks.onExportProject());

    const exportPngBtn = document.createElement("button");
    exportPngBtn.type = "button";
    exportPngBtn.className = "button button-primary button-sm";
    exportPngBtn.title = "Transparente PNG-Datei in Originalgröße herunterladen";
    exportPngBtn.innerHTML = `<span class="btn-icon">💾</span> <span class="btn-text">PNG speichern</span>`;
    exportPngBtn.addEventListener("click", () => this.callbacks.onExportPng());

    actionsGroup.appendChild(newBtn);
    actionsGroup.appendChild(clearBtn);
    actionsGroup.appendChild(importBtn);
    actionsGroup.appendChild(exportProjectBtn);
    actionsGroup.appendChild(exportPngBtn);
    this.element.appendChild(actionsGroup);
  }

  public updateHistory(undoCount: number, redoCount: number): void {
    this.undoBtn.disabled = undoCount === 0;
    this.redoBtn.disabled = redoCount === 0;
  }

  public setDirty(dirty: boolean): void {
    this.dirtyIndicator.style.display = dirty ? "inline" : "none";
  }

  public setDocumentDimensions(width: number, height: number): void {
    this.docTitle.textContent = `${width} × ${height} px`;
  }
}
